import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useSupaSWR } from '@packages/supabase'
import { useSupabase } from '@packages/supabase'
import { SortableTable } from '@packages/ui'
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react'
import i18n from '@packages/core/i18n'
import { useMemo, useState } from 'react'

interface PvDefaultCategoryFormValues {
  title: string
  image_url: string
  access: 'public' | 'private'
  parent_id: string | null
  enabled: boolean
}

const initialValues: PvDefaultCategoryFormValues = {
  title: '',
  image_url: '',
  access: 'public',
  parent_id: null,
  enabled: true
}

export const PreferencesAdministration = () => {
  const { supabase } = useSupabase()
  const [opened, { open, close }] = useDisclosure(false)
  const [editing, setEditing] = useState<any | null>(null)
  const [formValues, setFormValues] =
    useState<PvDefaultCategoryFormValues>(initialValues)
  const [saving, setSaving] = useState(false)

  const {
    data: defaultCategories = [],
    mutate,
    isLoading
  } = useSupaSWR<any[]>({
    key: 'pv-default-categories',
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pv_categories')
        .select('*')
        .eq('is_default', true)
        .order('title', { ascending: true })
      if (error) throw error
      return data || []
    }
  })

  const parentOptions = useMemo(() => {
    return [
      { value: '', label: i18n.t('none', { defaultValue: 'None' }) },
      ...defaultCategories.map((category) => ({
        value: String(category.id),
        label: String(category.title)
      }))
    ]
  }, [defaultCategories])

  const openCreate = () => {
    setEditing(null)
    setFormValues(initialValues)
    open()
  }

  const openEdit = (category: any) => {
    setEditing(category)
    setFormValues({
      title: String(category.title || ''),
      image_url: String(category.image_url || ''),
      access: (category.access as 'public' | 'private') || 'public',
      parent_id: category.parent_id ? String(category.parent_id) : null,
      enabled: !(category.meta?.disabled === true)
    })
    open()
  }

  const submit = async () => {
    if (!formValues.title.trim()) return
    setSaving(true)
    try {
      const payload = {
        title: formValues.title.trim(),
        image_url: formValues.image_url.trim() || null,
        access: formValues.access,
        parent_id: formValues.parent_id || null,
        is_default: true,
        vault_owner_id: null,
        meta: {
          disabled: !formValues.enabled
        }
      }

      if (editing?.id) {
        const { error } = await supabase
          .from('pv_categories')
          .update(payload)
          .eq('id', editing.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('pv_categories').insert(payload)
        if (error) throw error
      }

      await mutate()
      close()
      setEditing(null)
      setFormValues(initialValues)
    } catch (error: any) {
      window.alert(
        i18n.t('admin.preference_vault.save_error', {
          defaultValue: 'Failed to save default category: {{message}}',
          message: error?.message || 'Unknown error'
        })
      )
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm(
      i18n.t('admin.preference_vault.delete_confirm', {
        defaultValue:
          'Delete this default category? This affects all tenants using it.'
      })
    )
    if (!confirmed) return

    const { error } = await supabase.from('pv_categories').delete().eq('id', id)
    if (error) {
      window.alert(
        i18n.t('admin.preference_vault.delete_error', {
          defaultValue: 'Failed to delete default category: {{message}}',
          message: error.message
        })
      )
      return
    }
    await mutate()
  }

  return (
    <Stack gap="xl" c="white">
      <Group justify="space-between">
        <Title order={2}>
          {i18n.t('admin.preference_vault.title', {
            defaultValue: 'Preferences Administration'
          })}
        </Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
          {i18n.t('admin.preference_vault.create_default_category', {
            defaultValue: 'Create Default Category'
          })}
        </Button>
      </Group>

      <Text size="sm" c="dimmed">
        {i18n.t('admin.preference_vault.description', {
          defaultValue:
            'Manage the global default preference categories available across all tenants.'
        })}
      </Text>

      <SortableTable
        data={defaultCategories}
        loading={isLoading}
        columns={[
          { key: 'title', label: i18n.t('title', { defaultValue: 'Title' }) },
          {
            key: 'image_url',
            label: i18n.t('image', { defaultValue: 'Image URL' }),
            render: (value) => {
              const url = String(value || '')
              if (!url) return '-'
              return (
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-300 underline"
                >
                  {url.slice(0, 60)}
                  {url.length > 60 ? '...' : ''}
                </a>
              )
            }
          },
          {
            key: 'access',
            label: i18n.t('access', { defaultValue: 'Access' })
          },
          {
            key: 'meta',
            label: i18n.t('status', { defaultValue: 'Status' }),
            render: (value) =>
              value?.disabled
                ? i18n.t('disabled', { defaultValue: 'Disabled' })
                : i18n.t('enabled', { defaultValue: 'Enabled' })
          },
          {
            key: 'id',
            label: i18n.t('actions', { defaultValue: 'Actions' }),
            render: (_value, category) => (
              <Group gap="xs">
                <ActionIcon variant="subtle" onClick={() => openEdit(category)}>
                  <IconEdit size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => void handleDelete(String(category.id))}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            )
          }
        ]}
      />

      <Modal
        opened={opened}
        onClose={() => {
          close()
          setEditing(null)
        }}
        title={
          editing
            ? i18n.t('admin.preference_vault.edit_default_category', {
                defaultValue: 'Edit Default Category'
              })
            : i18n.t('admin.preference_vault.new_default_category', {
                defaultValue: 'New Default Category'
              })
        }
      >
        <Stack c="black">
          <TextInput
            label={i18n.t('admin.preference_vault.category_title', {
              defaultValue: 'Category title'
            })}
            value={formValues.title}
            onChange={(event) =>
              setFormValues((prev) => ({ ...prev, title: event.currentTarget.value }))
            }
            required
          />
          <TextInput
            label={i18n.t('admin.preference_vault.image_url', {
              defaultValue: 'Image URL'
            })}
            value={formValues.image_url}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                image_url: event.currentTarget.value
              }))
            }
          />
          <Select
            label={i18n.t('admin.preference_vault.access_level', {
              defaultValue: 'Access'
            })}
            data={[
              { value: 'public', label: 'public' },
              { value: 'private', label: 'private' }
            ]}
            value={formValues.access}
            allowDeselect={false}
            onChange={(value) =>
              setFormValues((prev) => ({
                ...prev,
                access: (value as 'public' | 'private') || 'public'
              }))
            }
          />
          <Select
            label={i18n.t('admin.preference_vault.parent_category', {
              defaultValue: 'Parent category'
            })}
            data={parentOptions}
            value={formValues.parent_id || ''}
            onChange={(value) =>
              setFormValues((prev) => ({
                ...prev,
                parent_id: value ? String(value) : null
              }))
            }
          />
          <Switch
            label={i18n.t('admin.preference_vault.enabled', {
              defaultValue: 'Enabled for tenants'
            })}
            checked={formValues.enabled}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                enabled: event.currentTarget.checked
              }))
            }
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={close}>
              {i18n.t('cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button onClick={() => void submit()} loading={saving}>
              {i18n.t('save', { defaultValue: 'Save' })}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}

export default PreferencesAdministration

